import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { tourHasSeen, tourMarkSeen } from "../lib/tour";

/**
 * TourHint — featherweight tooltip used for the 2-hint first-time tour.
 *
 * Behaves like a polite whisper: appears once per id per browser, fades
 * in soft, auto-dismisses on first tap anywhere OR after `dismissAfterMs`,
 * never returns. Visually a single line + tiny pulse-dot so it reads as
 * "alive UI", not "intrusive popover".
 *
 * Props:
 *   id            — stable string, e.g. "results-flick"
 *   children      — the hint copy
 *   show          — boolean gate (parent decides when conditions are met)
 *   anchor        — "top" | "bottom-rail" | "below-card" — placement
 *   dismissAfterMs— optional auto-dismiss timer (default 6000)
 */
export default function TourHint({
    id,
    children,
    show = false,
    anchor = "bottom-rail",
    dismissAfterMs = 6000,
}) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!show) return;
        if (tourHasSeen(id)) return;
        // Tiny delay so the hint feels triggered BY the user's behavior
        // (active sat at slot 0 for 3s) rather than an instant pop.
        const t = setTimeout(() => setOpen(true), 250);
        return () => clearTimeout(t);
    }, [show, id]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => {
            tourMarkSeen(id);
            setOpen(false);
        }, dismissAfterMs);
        // Tap anywhere also dismisses — captured at document level so the
        // hint vanishes the moment the user takes ANY action (which in
        // their head means "got it").
        const onAny = () => {
            tourMarkSeen(id);
            setOpen(false);
        };
        window.addEventListener("pointerdown", onAny, { once: true, capture: true });
        return () => {
            clearTimeout(t);
            window.removeEventListener("pointerdown", onAny, { capture: true });
        };
    }, [open, id, dismissAfterMs]);

    // Anchor presets — each one targets a known UI surface; keeps callers
    // from passing arbitrary CSS that could mis-align across devices.
    // We use FIXED positioning instead of absolute so the hint floats
    // reliably regardless of which parent container the consumer drops
    // it inside. The component only relies on viewport coordinates.
    const anchorStyles = {
        // Centered horizontally, lifted clear of the bottom rail
        "bottom-rail": "fixed bottom-[78px] left-1/2 -translate-x-1/2",
        // Centered horizontally, sits just above the on-screen keyboard /
        // action row on CardDetail
        "below-card": "fixed bottom-[88px] left-1/2 -translate-x-1/2",
        // Top of viewport (16px in from the top edge)
        top: "fixed top-[14px] left-1/2 -translate-x-1/2",
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`tour-hint-${id}`}
                    className={`${anchorStyles[anchor] || anchorStyles["bottom-rail"]} z-50 pointer-events-none flex items-center gap-2 px-3.5 py-2 rounded-full backdrop-blur-md`}
                    style={{
                        background: "rgba(20, 22, 26, 0.78)",
                        border: "1px solid rgba(212, 175, 110, 0.32)",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.32), 0 0 18px rgba(212, 175, 110, 0.18)",
                        whiteSpace: "nowrap",
                        maxWidth: "92vw",
                    }}
                >
                    <motion.span
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2.0, repeat: Infinity, ease: "easeInOut" }}
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: "#d4af6e" }}
                    />
                    <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-amber-100/90">
                        {children}
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
